import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useQueryParameters } from '@treams/treams-hooks';
import { AnalyticsUserFilter } from '@treams/treams-network';
import { GearIcon, Heading3, IconButton, Paragraph, Popover, tokens } from '@treams/treams-ui';

const FilterOption = styled(Box)<{ selected?: boolean; dimmed?: boolean }>`
	cursor: pointer;
	padding: ${tokens.padding.control}px;
	border-radius: ${tokens.radius.md}px;
	background-color: ${tokens.surface.white};
	box-shadow: 0 0 0
		${(props) => (props.selected ? `2px ${tokens.brand.border}` : `1px ${tokens.border.default}`)};

	&:not(:last-child) {
		margin-bottom: ${tokens.gap.list}px;
	}
`;

const OptionHeading = styled(Paragraph)`
	color: ${tokens.text.primary};
	font-weight: ${tokens.typography.weights.semibold};
	margin-bottom: ${tokens.gap.inline}px;
`;

const USER_FILTER_KEY = 'userFilter';
const USER_FILTER_NONE = 'none';
const USER_FILTERS: Record<string, AnalyticsUserFilter | undefined> = {
	atEndDate: AnalyticsUserFilter.ActiveAtEndDate,
};
// Reverse mapping
const USER_FILTER_VALUES: Record<string, string> = Object.fromEntries(
	Object.entries(USER_FILTERS).map(([key, value]) => [value, key]),
);
const USER_FILTER_DEFAULT = Object.values(USER_FILTERS)[0];

export function setUserFilterSearchParams(
	query: URLSearchParams,
	filter: AnalyticsUserFilter | undefined,
) {
	query.delete(USER_FILTER_KEY);
	query.set(USER_FILTER_KEY, filter ? USER_FILTER_VALUES[filter] : USER_FILTER_NONE);
}

export default function AnalyticsUserFilterSelector(props: {
	onChange: (filter: AnalyticsUserFilter | undefined) => void;
}) {
	const { onChange } = props;
	const { t } = useTranslation(undefined, { keyPrefix: 'dashboard.sidebar.filters.userFilter' });

	const filterQueryParameters = useQueryParameters([USER_FILTER_KEY]);
	const userFilter = filterQueryParameters[USER_FILTER_KEY];
	const [userFilterPreference, setUserFilterPreference] =
		useLocalStorage<AnalyticsUserFilter | null>(
			'analyticsUserFilterPreference',
			USER_FILTER_DEFAULT,
		);

	const [filter, setFilter] = useState<AnalyticsUserFilter | undefined>(
		userFilter
			? userFilter === USER_FILTER_NONE
				? undefined
				: USER_FILTERS[userFilter]
			: (userFilterPreference ?? undefined),
	);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const url = new URL(window.location.href);
		setUserFilterSearchParams(url.searchParams, filter);

		window.history.replaceState(null, '', url.toString());
		setUserFilterPreference(filter ?? null);
		onChange(filter);
	}, [filter, onChange, setUserFilterPreference]);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<Box p={3} width={400}>
					<Heading3 mb={3}>{t('title')}</Heading3>
					{Object.entries(USER_FILTERS).map(([key, value]) => (
						<FilterOption key={key} selected={filter === value} onClick={() => setFilter(value)}>
							<OptionHeading>{t(`${key}.heading`)}</OptionHeading>
							<Paragraph>{t(`${key}.explanation`)}</Paragraph>
						</FilterOption>
					))}
					<FilterOption selected={!filter} onClick={() => setFilter(undefined)}>
						<OptionHeading>{t('none.heading')}</OptionHeading>
						<Paragraph>{t('none.explanation')}</Paragraph>
					</FilterOption>
				</Box>
			}
			onDismiss={() => setVisible(false)}
		>
			<IconButton type="secondary" icon={<GearIcon />} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}
